// src/Components/Common/SelectedFilesDisplay.jsx
import React, { useState, useMemo } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Badge,
  Collapse,
  Input,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { motion, AnimatePresence } from "framer-motion";

const SelectedFilesDisplay = ({
  selectedFiles = [],
  allFiles = [],
  title = "Selected Files",
  showSearch = true,
  showFileDetails = false,
  onClearAll,
  maxDisplayFiles = 10,
  style = {},
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Get file details for selected files
  const selectedFileDetails = useMemo(() => {
    if (!allFiles.length) {
      // If no allFiles provided, return basic info
      return selectedFiles.map((fileId) => ({
        file_id: fileId,
        file_name: `File ${fileId}`,
        name: `File ${fileId}`,
      }));
    }

    return selectedFiles
      .map((fileId) => {
        const file = allFiles.find((f) => f.file_id === fileId);
        return (
          file || {
            file_id: fileId,
            file_name: `File ${fileId}`,
            name: `File ${fileId}`,
          }
        );
      })
      .filter((file) => {
        if (!searchTerm.trim()) return true;
        const fileName = file.file_name || file.name || "";
        return fileName.toLowerCase().includes(searchTerm.toLowerCase());
      });
  }, [selectedFiles, allFiles, searchTerm]);

  const totalSelectedCount = selectedFiles.length;
  const displayFiles = isExpanded
    ? selectedFileDetails
    : selectedFileDetails.slice(0, maxDisplayFiles);
  const hasMoreFiles =
    selectedFileDetails.length > maxDisplayFiles && !isExpanded;

  const handleClearAll = () => {
    if (
      onClearAll &&
      window.confirm("Are you sure you want to clear all selected files?")
    ) {
      onClearAll();
    }
  };

  return (
    <Card
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: "4px",
        marginTop: "16px",
        ...style,
      }}
    >
      <CardHeader>
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <h5
              className="mb-0 d-flex align-items-center"
              style={{ color: "#1a6d51" }}
            >
              <i
                className="ri-checkbox-circle-fill me-2"
                style={{ color: "#2F9E37" }}
              ></i>
              {title}
              <Badge
                color="success"
                className="ms-2"
                style={{
                  backgroundColor: "#2F9E37",
                  fontSize: "11px",
                  fontWeight: "600",
                }}
              >
                {totalSelectedCount}
              </Badge>
            </h5>
              {totalSelectedCount > 0 && (
                <Button
                  size="sm"
                  color="outline-primary"
                  onClick={() => setIsExpanded(!isExpanded)}
                  style={{
                    fontSize: "11px",
                    padding: "1px 8px",
                    borderRadius: "4px",
                    marginLeft: "10px",
                  }}
                >
                  <i
                    className={`ri-${
                      isExpanded ? "arrow-up-s" : "arrow-down-s"
                    }-line me-1`}
                  ></i>
                  {isExpanded ? "Hide All" : `Show All`}
                </Button>
              )}
          </div>

          <div className="d-flex align-items-center gap-2">
            {showSearch && totalSelectedCount > 5 && (
              <div>
                <InputGroup style={{ width: "250px" }}>
                  <InputGroupText style={{ borderColor: "#d1d5db" }}>
                    <i className="ri-search-line text-muted" />
                  </InputGroupText>
                  <Input
                    type="text"
                    placeholder="Search selected files..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      borderRadius: "0 6px 6px 0",
                      fontSize: "13px",
                      borderColor: "#d1d5db",
                    }}
                  />
                </InputGroup>
              </div>
            )}
            {onClearAll && (
              <Button
                size="sm"
                color="outline-danger"
                onClick={handleClearAll}
                style={{
                  fontSize: "11px",
                  padding: "4px 8px",
                  borderRadius: "4px",
                }}
              >
                <i className="ri-close-circle-line me-1"></i>
                Clear All
              </Button>
            )}



          </div>
        </div>
      </CardHeader>

      <CardBody style={{ padding: "16px" }}>
        {/* Search for selected files */}

        {/* Selected files list */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
            gap: "8px",
          }}
        >
          <AnimatePresence>
            {displayFiles.map((file, index) => (
              <motion.div
                key={file.file_id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: index * 0.02 }}
                style={{
                  padding: "4px 8px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "10px",
                }}
              >
                <span
                  style={{
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontWeight: "500",
                    color: "#1a6d51",
                  }}
                  title={file.file_name || file.name || `File ${file.file_id}`}
                >
                  {file.file_name || file.name || `File ${file.file_id}`}
                </span>
                {showFileDetails && file.file_description && (
                  <i
                    className="ri-information-line text-muted"
                    title={file.file_description}
                    style={{ fontSize: "12px" }}
                  ></i>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* No search results */}
        {searchTerm && selectedFileDetails.length === 0 && (
          <div
            className="text-center"
            style={{
              padding: "20px",
              color: "#6b7280",
              fontSize: "13px",
            }}
          >
            <i className="ri-search-line me-2"></i>
            No selected files match your search
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default SelectedFilesDisplay;
